/**
 * The single HTTP path to the backend: base URL, bearer token, refresh-on-401
 * behind a mutex (so ten parallel 401s trigger one refresh), and the backend
 * envelope `{ status, success, message: string[], customCode, data }` unwrapped
 * to `data`. Ported from deepdive-v2's apiSlice with its bugs fixed.
 *
 * deepdive-v2 services often catch their own failures and *return*
 * `{ success: false, message, error }` with HTTP 200; that is turned into an
 * error here, so no loader ever parses a failure as data.
 */
import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';

import { env } from '@/config/env';
import { fromFetchError, type ApiError } from '@/lib/errors';
import { readAuthTokens } from '@/services/authTokens';
import { tokenStorage } from '@/services/tokenStorage';

const mutex = new Mutex();

const rawBaseQuery = fetchBaseQuery({
  baseUrl: env.apiBaseUrl,
  prepareHeaders: (headers) => {
    // A request that names its own credential keeps it.
    if (headers.has('Authorization')) return headers;
    const token = tokenStorage.getAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return headers;
  },
});

/** v3 auth issues, refreshes and revokes tokens; the v2 routes still read accept them too. */
export const REFRESH_URL = '/v3/auth/refresh';

/** The auth calls themselves: a 401 from one of them is the answer, not a stale token. */
const NO_REFRESH = ['/v3/auth/login', '/v3/auth/register', REFRESH_URL, '/v3/auth/logout'];

type Envelope<T> = {
  success?: boolean;
  data?: T;
  message?: string[] | string;
  customCode?: string;
  error?: string;
};

type RawResult = Awaited<ReturnType<typeof rawBaseQuery>>;

export function unwrap(result: RawResult): RawResult {
  if (result.error || result.data === undefined) return result;
  const body = result.data as Envelope<unknown>;
  if (!body || typeof body !== 'object' || Array.isArray(body)) return result;
  if (body.success === false) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: body.error ?? 'The server reported a failure.',
        data: body,
      },
      meta: result.meta,
    };
  }
  // Only a pure envelope is unwrapped. One with fields beside `data` (v2's
  // `pagination`, `has_comparison`…) is passed whole, or they would be lost.
  if ('success' in body && 'data' in body && Object.keys(body).every((k) => ENVELOPE_KEYS.has(k))) {
    return { ...result, data: body.data };
  }
  return result;
}

const ENVELOPE_KEYS = new Set(['success', 'message', 'data', 'customCode', 'status', 'statusCode']);

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await rawBaseQuery(args, api, extraOptions);
  const url = typeof args === 'string' ? args : args.url;

  if (result.error?.status === 401 && !NO_REFRESH.some((path) => url.includes(path))) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshToken = tokenStorage.getRefreshToken();
        const refreshed = refreshToken
          ? await rawBaseQuery(
              { url: REFRESH_URL, method: 'POST', body: { refreshToken } },
              api,
              extraOptions,
            )
          : null;
        const unwrapped = refreshed ? unwrap(refreshed) : null;
        const tokens = unwrapped && !unwrapped.error ? readAuthTokens(unwrapped.data) : null;
        if (tokens) {
          tokenStorage.setTokens(tokens.accessToken, tokens.refreshToken);
          result = await rawBaseQuery(args, api, extraOptions);
        } else {
          // Ends the session; RequireAuth (subscribed to tokenStorage) sends the user to /login.
          tokenStorage.clear();
          api.dispatch({ type: 'auth/sessionExpired' });
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      // Retry with the refreshed token — unless that refresh failed and ended the session.
      if (tokenStorage.hasSession()) result = await rawBaseQuery(args, api, extraOptions);
    }
  }
  return unwrap(result);
};

/** What the api uses: reauth + envelope unwrap, with errors normalised to `ApiError`. */
export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, ApiError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await baseQueryWithReauth(args, api, extraOptions);
  if (result.error) return { error: fromFetchError(result.error), meta: result.meta };
  return { data: result.data, meta: result.meta };
};

export type AppBaseQuery = typeof baseQuery;
